/* eslint-disable prefer-arrow-callback */
import { useCallback, useReducer } from "react";

export type ScenarioAnalysisModal = "fieldSoilExplainer" | "howThisWorks" | "sideBySideExplainer" | "keyInsights" | "projectSettings" | "fieldReselect";

export type ScenarioAnalysisModalsOpen = Record<ScenarioAnalysisModal, boolean>;

type ModalState = {
  open: ScenarioAnalysisModalsOpen;
  /** The scenario the key insights modal opens on: the one the box it was opened from was showing. */
  keyInsightsScenarioId: string | null;
};

type ModalAction =
  | { type: "open"; modal: ScenarioAnalysisModal }
  | { type: "close"; modal: ScenarioAnalysisModal }
  | { type: "openKeyInsights"; scenarioId: string | null };

const INITIAL_MODAL_STATE: ModalState = {
  open: {
    fieldSoilExplainer: false,
    howThisWorks: false,
    sideBySideExplainer: false,
    keyInsights: false,
    projectSettings: false,
    fieldReselect: false,
  },
  keyInsightsScenarioId: null,
};

const setOpen = (state: ModalState, modal: ScenarioAnalysisModal, isOpen: boolean): ModalState =>
  // Same state when nothing changes, so a repeat open/close does not re-render (as a useState setter would bail out).
  state.open[modal] === isOpen ? state : { ...state, open: { ...state.open, [modal]: isOpen } };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case "open":
      return setOpen(state, action.modal, true);
    case "close":
      return setOpen(state, action.modal, false);
    case "openKeyInsights":
      return { ...setOpen(state, "keyInsights", true), keyInsightsScenarioId: action.scenarioId };
    default:
      return state;
  }
};

/** Open state of the analysis page's modals and drawers. */
export const useModalState = () => {
  const [{ open: openModals, keyInsightsScenarioId }, dispatch] = useReducer(modalReducer, INITIAL_MODAL_STATE);

  const openModal = useCallback(function openModal(modal: ScenarioAnalysisModal) {
    dispatch({ type: "open", modal });
  }, []);
  const closeModal = useCallback(function closeModal(modal: ScenarioAnalysisModal) {
    dispatch({ type: "close", modal });
  }, []);
  const openKeyInsights = useCallback(function openKeyInsights(scenarioId: string | null) {
    dispatch({ type: "openKeyInsights", scenarioId });
  }, []);

  return { openModals, keyInsightsScenarioId, openModal, closeModal, openKeyInsights };
};
